import _ from 'lodash'
import { get } from 'lodash'
import { Observable } from 'rxjs'
import { first, take, map } from 'rxjs/operators'
import blobStream from 'blob-stream'
import Konva from 'konva'
import PDFDocument from "pdfkit/js/pdfkit.standalone.js"

import { SearchFeatured, getTranslatedString, ConditionReport, Alteration, Wemi, CounterSignatureResource, computeAlterationsIndexes, Item, AssociatedMediasResource, Media, AssociatedMedias, CrPDFSection } from '@skinsoft/types'
import { thesaurusTermSelectors, conditionReportSelectors, wemiSelectors, aliasSelectors, locationSelectors, itemSelectors, mediaSelectors } from '@skinsoft/selectors'
import { AliasDataStore, MediaDataStore, ThesaurusTermDataStore, LocationDataStore, ItemDataStore, InstitutionDataStore } from '@skinsoft/entity'
import { isImageOrientationAutoManaged, toFormat, getServerDateFormat, DATE_UNIT, getAppLocaleFromI18nLocale, getDateString } from '@skinsoft/utils'
import { bus } from '@skinsoft/bus'
import { extractAndRemoveExifOrientationFromMedia, getImageOrientation, ImageTransferHelper, MediaTransferHelper, convertBase64ToArrayBuffer } from '@skinsoft/media-transfer-helper'

import { DrawingToolType } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { getLabelCoordinates } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox/label-coordinates-helper'
import './pdf-document-extension-methods'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'

declare const config
const FALLBACK_LOCALE: string = config.fallback_locale
const PRINT_NON_ALTERATION_MEDIAS: boolean = config.conditionReportPDFGenerator.alterations.showNonAlterationMedias
const NON_ALTERATION_MEDIAS_ORDER: "after" | "before" = config.conditionReportPDFGenerator.alterations.nonAlterationMediasOrder
const SECTIONS_ORDER: CrPDFSection[] = config.conditionReportPDFGenerator.sections.order
const ALTERATION_MEDIA_TYPE: string = config.conditionReportPDFGenerator.alterations.mediaType
const COMPUTE_PIXEL_RATIO: boolean = config.conditionReportPDFGenerator.alterations.computePixelRatio
const PIXEL_RATIO: number = config.conditionReportPDFGenerator.alterations.pixelRatio
const TARGET_PIXEL_NUMBER: number = config.conditionReportPDFGenerator.alterations.targetPixelNumber
const IGNORE_ITEM_MEDIA_ERRORS: boolean = config.conditionReportPDFGenerator.ignoreItemMediaErrors

enum AlterationsDisplayMode {
    SUMMARY,
    WITH_THUMBNAILS,
    FULL_PAGE
}

export interface SignatureDrawing {
    canvas: { width: number, height: number },
    signature: any[]
}

interface ConstructorOptions {
    topicToEmitProgress: string,
    defaultSignatrueCanvasWidth: number,
    defaultSignatrueCanvasHeight: number,
    i18nMessages,
    i18nDatesTimeFormat,
    i18nFallBackConf: { [locale: string]: string[] },
}

interface AlterationNameAndDetailedViews {
    detailedViews: string[],
    alterationName: string,
    alterationNumber: number,
}

enum DetailedVuePlacement {
    AFTER_EACH = "AFTER_EACH",
    END = "END",
}

export class ConditionReportPDFGenerator {

    // Layout constants
    public static readonly TITLE_FONT_SIZE = 19;
    public static readonly SECTION_FONT_SIZE = 16;
    public static readonly BODY_TEXT_FONT_SIZE = 11;
    public static readonly FOOTER_FONT_SIZE = 10;
    public static readonly DOCUMENT_MARGIN = 35;
    public static readonly LOGO_MARGIN_Y = -50
    public static readonly LOGO_MARGIN_X = 15
    public static readonly A4_PAPER_SIZE = [595.28, 841.89];
    public static readonly DEFAULT_IMAGE_SIZE = 160;
    public static readonly FULL_PAGE_IMAGE_SIZE = 400;
    public static readonly ALTERATION_IMAGE_SIZE = 450
    public static readonly IMAGE_BETWEEN_GAP = 15;
    public static readonly IMAGE_TEXT_BETWEEN_GAP = 30;
    public static readonly ALTERATION_TEXT_BETWEEN_COL_GAP = 30
    public static readonly SIGNATURE_IMAGE_WIDTH = 250;
    public static readonly IMAGE_GENERATION_TARGET_PIXEL_NUMBER = TARGET_PIXEL_NUMBER
    public static readonly SIGNATURE_GENERATION_PIXEL_RATIO = 0.5;
    public static SIGNATURE_IMAGE_HEIGHT
    public static PRINT_IMAGES_OUTLINE = false
    public static readonly PRINT_LOGO = true
    public static readonly LOGO_SIZE = 150

    public static DETAILED_VIEW_IMAGE_SIZE = 150 // image size is calculated later according to images per row and images per col
    public static readonly DETAILED_VIEW_IMAGES_PER_ROW = 3
    public static readonly DETAILED_VIEW_IMAGES_PER_COLUMN = 4
    public static readonly DETAILED_VIEW_MIN_SPACING = 25
    public static DETAILED_VIEW_ROW_GAP = ConditionReportPDFGenerator.DETAILED_VIEW_MIN_SPACING
    public static DETAILED_VIEW_COL_GAP = ConditionReportPDFGenerator.DETAILED_VIEW_MIN_SPACING
    public static DETAILED_VIEW_PLACEMENT: DetailedVuePlacement = DetailedVuePlacement.END

    public static readonly REGULAR_FONT: string = "Helvetica";
    public static readonly BOLD_FONT: string = "Helvetica-Bold";

    private hiddenCanvasContainer: HTMLDivElement = null as any;
    private hiddenStage: Konva.Stage = null as any;
    private hiddenStageShapesLayer: Konva.Layer = null as any;
    private hiddenStageAnnotationsLayer: Konva.Layer = null as any;

    // For annotations, see their equivalents in the drawing editor
    private static readonly STROKE_WIDTH_RATIO: number = 0.01;
    private static readonly ANNOTATION_LABEL_RADIUS_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_FONT_SIZE_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_STROKE_WIDTH_RATIO = 0.004;

    private static I18N_MESSAGES
    private static I18N_DATES_TIME_FORMAT
    private static I18N_FALLBACK_CONF: { [locale: string]: string[] }
    private static appLocale = FALLBACK_LOCALE
    private static i18nLocale = FALLBACK_LOCALE

    private pageNumber: number = 0;
    private isDryRun: boolean = false
    private static TOPIC_TO_EMIT_PROGRESS: string | null

    public static getInstance() {
        if (!ConditionReportPDFGenerator.instance) {
            ConditionReportPDFGenerator.instance = new ConditionReportPDFGenerator()
        }

        return ConditionReportPDFGenerator.instance
    }

    public static install(Vue, options: ConstructorOptions) {
        this.I18N_MESSAGES = options.i18nMessages
        this.I18N_DATES_TIME_FORMAT = options.i18nDatesTimeFormat
        this.I18N_FALLBACK_CONF = options.i18nFallBackConf

        this.TOPIC_TO_EMIT_PROGRESS = options.topicToEmitProgress
        this.SIGNATURE_IMAGE_HEIGHT = ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH *
            options.defaultSignatrueCanvasHeight / options.defaultSignatrueCanvasWidth;

        // Compute length of image and spacing for detailed views
        const writableWidth = this.A4_PAPER_SIZE[0] - (2 * this.DOCUMENT_MARGIN)
        const totalSpacingDetailedViewRow = (this.DETAILED_VIEW_IMAGES_PER_ROW - 1) * this.DETAILED_VIEW_MIN_SPACING

        const detailedViewMaxImageSizeRow = (writableWidth - totalSpacingDetailedViewRow) / this.DETAILED_VIEW_IMAGES_PER_ROW

        const writableLength = this.A4_PAPER_SIZE[1] - (2 * this.DOCUMENT_MARGIN)
        const totalSpacingDetailedViewCol = Math.trunc((this.DETAILED_VIEW_IMAGES_PER_COLUMN - 1) * this.DETAILED_VIEW_MIN_SPACING)

        const detailedViewMaxImageSizeCol = Math.trunc((writableLength - totalSpacingDetailedViewCol) / this.DETAILED_VIEW_IMAGES_PER_COLUMN)

        if(detailedViewMaxImageSizeCol === detailedViewMaxImageSizeRow) {
            this.DETAILED_VIEW_IMAGE_SIZE = detailedViewMaxImageSizeRow
        }
        else if(detailedViewMaxImageSizeCol > detailedViewMaxImageSizeRow) {
            this.DETAILED_VIEW_IMAGE_SIZE = detailedViewMaxImageSizeRow
            this.DETAILED_VIEW_COL_GAP = (writableWidth - (this.DETAILED_VIEW_IMAGES_PER_ROW * this.DETAILED_VIEW_IMAGE_SIZE)) / (this.DETAILED_VIEW_IMAGES_PER_ROW - 1)
        } else if(detailedViewMaxImageSizeCol < detailedViewMaxImageSizeRow) {
            this.DETAILED_VIEW_IMAGE_SIZE = detailedViewMaxImageSizeCol
            this.DETAILED_VIEW_ROW_GAP = (writableLength - (this.DETAILED_VIEW_IMAGES_PER_COLUMN * this.DETAILED_VIEW_IMAGE_SIZE)) / (this.DETAILED_VIEW_IMAGES_PER_COLUMN - 1)
        } 

        this.DETAILED_VIEW_IMAGE_SIZE = _.min([detailedViewMaxImageSizeCol, detailedViewMaxImageSizeRow])!

        Vue.prototype.$conditionReportPDFGenerator = ConditionReportPDFGenerator.getInstance()
    }

    private static instance: ConditionReportPDFGenerator = null as any

    // tslint:disable-next-line:member-ordering
    public generatePDF(conditionReport: ConditionReport, associatedItem: Wemi, onPdfGenerated, locale?: string, excludedMediaIds?: string[], filterPdfs: boolean = true) {
        if(locale) {
            ConditionReportPDFGenerator.appLocale = getAppLocaleFromI18nLocale(locale) ?? locale
            ConditionReportPDFGenerator.i18nLocale = locale
        } else {
            ConditionReportPDFGenerator.appLocale = getAppLocaleFromI18nLocale(FALLBACK_LOCALE) ?? FALLBACK_LOCALE
            ConditionReportPDFGenerator.i18nLocale = FALLBACK_LOCALE
        }

        return new Promise<void>(async (resolve, reject) => {
            try {
                const cleanedConditionReport = await this.cleanConditionReport(conditionReport, filterPdfs, excludedMediaIds)
                this.createHiddenCanvas()

                const [logoBase64, searchFeatured] = await Promise.all([
                    this.prefetchLogo(),
                    toSearchFeatured$(associatedItem as Wemi, ConditionReportPDFGenerator.appLocale).pipe(first()).toPromise(),
                ])

                const totalPages = await this.countTotalPages(cleanedConditionReport, associatedItem, searchFeatured)

                const doc = new PDFDocument({
                    autoFirstPage: false,
                    compress: true,
                    margin: ConditionReportPDFGenerator.DOCUMENT_MARGIN,
                    size: ConditionReportPDFGenerator.A4_PAPER_SIZE,
                })

                const refNumber = conditionReportSelectors.referenceNumber(cleanedConditionReport) as string
                this.pageNumber = 0
                doc.on('pageAdded', () => this.createPageHeaderAndFooter(doc, logoBase64, refNumber, totalPages))

                const stream = doc.pipe(blobStream())
                doc.addPage()

                stream.on('finish', () => {
                    onPdfGenerated(stream.toBlob('application/pdf'))
                    resolve()
                })

                await this.drawPDF(doc, cleanedConditionReport, associatedItem, searchFeatured)
            } catch(err) {
                this.isDryRun = false
                console.debug(err)
                reject(err)
            }
        })
    }

    // Fetches the institution logo as base64 before PDF generation so it can be
    // used synchronously inside the pageAdded event handler.
    private async prefetchLogo(): Promise<string | null> {
        if (!ConditionReportPDFGenerator.PRINT_LOGO) return null
        try {
            const logoId = await InstitutionDataStore.getInstance().getInstitutionLogoId().pipe(first()).toPromise()
            if (!logoId) return null
            const isLocalMedia = logoId.startsWith('local:')
            const media = await ImageTransferHelper.getInstance()
                .downloadImage(logoId, 'vignette250', isLocalMedia)
                .pipe(take(1)).toPromise()
            return media?.dataUrl ?? null
        } catch (err) {
            console.log(err)
            return null
        }
    }

    private createDiscardStream() {
        const listeners: Record<string, (() => void)[]> = {}
        return {
            write: (_chunk: any) => {},
            end: (_chunk?: any, _encoding?: any, cb?: any) => {
                ;(listeners['finish'] ?? []).forEach(fn => fn())
                if (typeof cb === 'function') cb()
            },
            on(event: string, cb: () => void) { (listeners[event] ?? (listeners[event] = [])).push(cb); return this },
            once(event: string, cb: () => void) { (listeners[event] ?? (listeners[event] = [])).push(cb); return this },
            removeListener(_e: string, _cb: any) { return this },
            emit(_event: string, ..._args: any[]) { return this },
        }
    }

    // Dry-run pass: runs the full layout without loading images to count pages.
    // Images restore doc.y so skipping them produces the same page breaks.
    private async countTotalPages(conditionReport: ConditionReport, associatedItem: Wemi, searchFeatured: SearchFeatured): Promise<number> {
        let count = 0
        const doc = new PDFDocument({
            autoFirstPage: false,
            compress: true,
            margin: ConditionReportPDFGenerator.DOCUMENT_MARGIN,
            size: ConditionReportPDFGenerator.A4_PAPER_SIZE,
        })
        doc.on('pageAdded', () => count++)
        const stream = doc.pipe(this.createDiscardStream())

        this.isDryRun = true
        doc.addPage()
        await this.drawPDF(doc, conditionReport, associatedItem, searchFeatured)
        await new Promise<void>((resolve, reject) => {
            stream.on('finish', resolve)
            stream.on('error', reject)
        })
        this.isDryRun = false

        return count
    }

    private createPageHeaderAndFooter(doc, logoBase64: string | null, refNumber: string, totalPages: number): void {
        const pageNum = ++this.pageNumber
        const savedX = doc.x
        const savedY = doc.y

        doc.save()

        if (logoBase64) {
            const savedTop = doc.page.margins.top
            doc.page.margins.top = 0
            this.addImageFromBase64(
                doc,
                logoBase64,
                doc.page.width - ConditionReportPDFGenerator.LOGO_SIZE - ConditionReportPDFGenerator.LOGO_MARGIN_X,
                ConditionReportPDFGenerator.LOGO_MARGIN_Y,
                ConditionReportPDFGenerator.LOGO_SIZE
            )
            doc.page.margins.top = savedTop
        }

        const savedBottom = doc.page.margins.bottom
        doc.page.margins.bottom = 0
        doc
            .fill('black')
            .font(ConditionReportPDFGenerator.REGULAR_FONT)
            .fontSize(ConditionReportPDFGenerator.FOOTER_FONT_SIZE)
            .text(
                `${this.$t('pdfDocument.footer.prefix')} ${refNumber} ${this.$t('pdfDocument.footer.separator')} ${pageNum}/${totalPages}`,
                20,
                doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN,
                { align: 'right' }
            )
        doc.page.margins.bottom = savedBottom

        doc.restore()
        doc.x = savedX
        doc.y = savedY
    }

    private async cleanConditionReport(conditionReport: ConditionReport, filterPdfs: boolean, excludedMediaIds?: string[]): Promise<ConditionReport> {
        const cleanedConditionReport: ConditionReport = _.cloneDeep(conditionReport)

        if(excludedMediaIds?.length) {
            cleanedConditionReport.associatedMedias.medias = cleanedConditionReport.associatedMedias.medias?.filter(mediaId => {
                return !excludedMediaIds.includes(mediaId)
            })
        }

        if(filterPdfs) {
            const cleanedAssociatedMedias = await this.removePDFsFromMedias(cleanedConditionReport.associatedMedias)
            cleanedConditionReport.associatedMedias = cleanedAssociatedMedias as AssociatedMedias
        }
        
        return cleanedConditionReport
    }

    /**
     * Creates a unique hidden canvas to perform drawing operations
     * before exporting images to pdf.
     */
    private  createHiddenCanvas() {
        if (!this.hiddenCanvasContainer) {
            this.hiddenCanvasContainer = document.createElement("div");
            this.hiddenCanvasContainer.id = 'container';
            this.hiddenCanvasContainer.hidden = true;
            document.body.appendChild(this.hiddenCanvasContainer);

            // Creating konva stage to render image
            const stageConfig = {
                container: 'container',
                height: 50,
                width: 50,
            }

            this.hiddenStage = new Konva.Stage(stageConfig);
            this.hiddenStageShapesLayer = new Konva.Layer({ listening: false })
            this.hiddenStageAnnotationsLayer = new Konva.Layer({ listening: false })
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStage.add(this.hiddenStageAnnotationsLayer)
        }
    }

    // Resets everything a consumer of the hidden stage could leave behind (children, scale, size),
    // so callers can rely on it both to start from a clean stage and to release it after use.
    private resetHiddenCanvases() {
        this.hiddenStageShapesLayer.destroyChildren()
        this.hiddenStageAnnotationsLayer.destroyChildren()
        this.hiddenStageShapesLayer.scale({ x: 1, y: 1 })
        this.hiddenStageAnnotationsLayer.scale({ x: 1, y: 1 })
        // resizing the stage to free memory
        this.hiddenStage.width(1)
        this.hiddenStage.height(1)
    }

    private  async drawPDF(doc, conditionReport: ConditionReport, wemi: Wemi, searchFeatured: SearchFeatured) {

        await this.createDocumentHeader(doc, conditionReportSelectors.referenceNumber(conditionReport) as string)

        for(const [index, section] of SECTIONS_ORDER.entries()) {
            switch(section.type) {
                case "alteration":
                    await this.createAlterationsSection(doc, conditionReport)
                    break
                case "context":
                    await this.createContextSection(doc, conditionReport, section.startOnNewPage, index)
                    break
                case "item":
                    await this.createItemSection(doc, wemi, searchFeatured, section.startOnNewPage, index)
                    break
                case "report":
                    await this.createReportSection(doc, conditionReport, section.startOnNewPage, index)
                    break
            }

            if(!this.isDryRun && ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
                bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: index + 1 })
            }
        }

        doc.end();
    }

    private  async createDocumentHeader(doc, conditionReportTitle) {
        doc.moveDown(8);
        doc.font(ConditionReportPDFGenerator.BOLD_FONT)
        const titlePrefix = this.$t('pdfDocument.mainTitlePrefix')

        doc.fontSize(ConditionReportPDFGenerator.TITLE_FONT_SIZE).text(`${titlePrefix}${conditionReportTitle}`, {
            align: 'center'
        });
    }

    private  async createItemSection(doc, wemi: Wemi, searchFeatured: SearchFeatured, startOnNewPage: boolean = true, sectionIndex?: number) {
        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN
        if(startOnNewPage) {
            doc.addPage()
        } else if(sectionIndex) {
            doc.moveDown(1)
        }
        let indentation = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        let textWidth = doc.page.width - 2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN;

        doc.sectionTitle(this.$t('pdfDocument.objectSection.sectionTitle'));
        doc.moveDown(2)
        const yImage = doc.y;
        if (searchFeatured.media) {
            indentation = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_TEXT_BETWEEN_GAP + ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            textWidth = doc.page.width - indentation - ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            // If there is an error to get object image, just ignore it
            try {
                await this.addImageFromMediaId(doc, searchFeatured.media)
            } catch(err) {
                // LOG
                console.debug("PDFGenerator: Error with linked item first media: ", err)
                if(!IGNORE_ITEM_MEDIA_ERRORS) {
                    throw err
                }
            }
        }

        let alias: string = searchFeatured.credits ? searchFeatured.credits.alias : "";
        const authorFunction: string = searchFeatured.credits ? searchFeatured.credits.authorFunction : "";
        if (alias) {
            alias = await this.getAliasFromId(alias)
        }
        const authorSummary = (alias || "") + (alias && authorFunction ? ` (${authorFunction})` : "")

        doc.y = yImage;
        doc.x = indentation;


        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.inventoryNumber'), 
            searchFeatured.inventoryNumber, 
            { width: textWidth }
        )

        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.title'), 
            wemiSelectors.descriptionMuseumWorkTitles(wemi, ConditionReportPDFGenerator.appLocale), 
            { width: textWidth }
        )

        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.author'), 
            authorSummary, 
            { width: textWidth }
        )

        let formattedDate = ''
        const skindate = wemiSelectors.creationDateSkinDate(wemi)
        formattedDate = getDateString(skindate, ConditionReportPDFGenerator.appLocale)
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.creationDate'), 
            formattedDate, 
            { width: textWidth }
        )
            

        await this.writeDomainItemSection(doc, wemi, textWidth)
        await this.writeDenominationItemSection(doc, wemi, textWidth)
        await this.writeIdentificationNotesSection(doc, wemi, textWidth)
        await this.writeMaterialsItemSection(doc, wemi, textWidth)
        await this.writeTechniquesItemSection(doc, wemi, textWidth)
        await this.writeDimensionsItemSection(doc, wemi, textWidth)
        await this.writeDescriptionsItemSection(doc, wemi, textWidth)
        await this.writeConditioningItemSection(doc, wemi, textWidth)
        await this.writeInstitutionItemSection(doc, wemi, textWidth)
    }

    private async writeDomainItemSection(doc, wemi: Wemi, textWidth: number) {
        let domainString: string = '' 
        const domains = wemiSelectors.domains(wemi)
        if(domains) {
            const separator = ', '
            for(const domain of domains) {
                const value = await this.getThesaurusTermFromId(domain)
                if(value) {
                    domainString += `${value}${separator}`
                }
            }
            domainString = domainString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.domains'), 
            domainString,
            { width: textWidth }
        )
    }

    private async writeDenominationItemSection(doc, wemi: Wemi, textWidth: number) {
        let denominationString: string = ''
        const denominations = wemiSelectors.denominations(wemi)
        if(denominations) {
            const separator = ', '

            for(const denomination of denominations) {
                let denominationValue = ''
                let precision = ''

                if(denomination.denomination) {
                    denominationValue = await this.getThesaurusTermFromId(denomination.denomination)
                }
                if(denomination.precision) {
                    precision = getTranslatedString(
                        denomination.precision,
                        ConditionReportPDFGenerator.appLocale
                    )
                }

                if(denominationValue) {
                    denominationString += denominationValue
                }
                if(precision) {
                    denominationString += ` (${precision})`
                }
                if(denominationValue || precision) {
                    denominationString += separator
                }
            }
            denominationString = denominationString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.typology'), 
            denominationString,
            { width: textWidth }
        )
    }

    private async writeIdentificationNotesSection(doc, wemi: Wemi, textWidth: number) {
        let identificationString = ''
        const notesOnIdentification = wemiSelectors.notesOnIdentification(wemi)
        if(notesOnIdentification?.length) {
            const separator = ', '

            for(const note of notesOnIdentification) {
                let noteValue: string = ''
                if(note.note) {
                    noteValue = getTranslatedString(note.note, ConditionReportPDFGenerator.appLocale)
                }

                if(noteValue) {
                    identificationString += `${noteValue}${separator}`
                }
            }
            identificationString = identificationString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.identification'),
            identificationString, 
            { width: textWidth }
        )
    }

    private async writeMaterialsItemSection(doc, wemi: Wemi, textWidth: number) {
        const materials = wemiSelectors.materials(wemi)
        let materialsString: string = ''
        if(materials?.length) {
            const separator = ', '

            for(const material of materials) {
                let materialValue: string | null = null
                let precision: string | null = null
                if(material.material) {
                    materialValue = await this.getThesaurusTermFromId(material.material)
                }
                if(material.precision) {
                    precision = getTranslatedString(material.precision, ConditionReportPDFGenerator.appLocale)
                }

                if(materialValue) {
                    materialsString += materialValue
                }
                if(precision) {
                    materialsString += `(${precision})`
                }
                if(materialValue || precision) {
                    materialsString += separator
                }
            }
            materialsString = materialsString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.materials'),
            materialsString, 
            { width: textWidth }
        )
    }

    private async writeTechniquesItemSection(doc, wemi: Wemi, textWidth: number) {
        const techniques = wemiSelectors.techniques(wemi)
        let techniquesString: string = ''
        const separator = ', '

        if(techniques?.length) {
            for(const technique of techniques) {
                let techniqueValue: string | null = null
                let precision: string | null = null
                if(technique.technique) {
                    techniqueValue = await this.getThesaurusTermFromId(technique.technique)
                }
                if(technique.precision) {
                    getTranslatedString(technique.precision, ConditionReportPDFGenerator.appLocale)
                }

                if(techniqueValue) {
                    techniquesString += techniqueValue
                }
                if(precision) {
                    techniquesString += `(${precision})`
                }
                if(techniqueValue || precision) {
                    techniquesString += separator
                }
            }
        }
        techniquesString = techniquesString.slice(0, -separator.length)
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.techniques'),
            techniquesString, 
            { width: textWidth }
        )
    }

    private async writeDimensionsItemSection(doc, wemi: Wemi, textWidth: number) {
        let measurementsString: string = ''
        const measurements = wemiSelectors.measurements(wemi)
        if(measurements?.length) {
            for(const measurement of measurements) {
                if(measurement.measures) {
                    for(const measure of measurement.measures) {
                        let typeOfMeasure: string = ''
                        let value: string = ''
                        let unit: string = ''
                        
                        if(measure.typeOfMeasure) {
                            typeOfMeasure = await this.getThesaurusTermFromId(measure.typeOfMeasure)
                        }
                        if(measure.value) {
                            value = getTranslatedString(
                                measure.value, 
                                ConditionReportPDFGenerator.appLocale
                            )
                        }
                        if(measure.unit) {
                            unit = await this.getThesaurusTermFromId(measure.unit)
                        }

                        if(typeOfMeasure || value) {
                            measurementsString += `${typeOfMeasure} : ${value} ${unit}\n`
                        }
                    }
                }
                if(measurement.precision) {
                    const precision: string = getTranslatedString(
                        measurement.precision,
                        ConditionReportPDFGenerator.appLocale
                    )
                    if(precision) {
                        measurementsString += `${precision}\n`
                    }
                }
            }
        }
        if(measurementsString) {
            doc.boldText(
                this.$t('pdfDocument.objectSection.dimensions'), 
                { width: textWidth }
            )
            doc.regularText(measurementsString, { width: textWidth, indent: 10 })
            // does not print anything but resets spacing
            doc.moveDown(1)
        }
    }

    private async writeDescriptionsItemSection(doc, wemi: Wemi, textWidth: number) {
        let descriptionString = ''
        const descriptions = wemiSelectors.descriptions(wemi)
        if(descriptions) {
            const separator = ', '
            for(const description of descriptions) {
                if(description.content) {
                    const value: string = getTranslatedString(
                        description.content,
                        ConditionReportPDFGenerator.appLocale
                    )

                    if(value) {
                        descriptionString += `${value}${separator}`
                    }
                }
            }
            descriptionString = descriptionString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.descriptions'),
            descriptionString, 
            { width: textWidth }
        )
    }

    private async writeConditioningItemSection(doc, wemi: Wemi, textWidth: number) {
        let containersString = ''
        const conditioningMaterialSeparator = " ; "

        const conditioningMaterial = wemiSelectors.conditioningMaterial(wemi, ConditionReportPDFGenerator.appLocale)
        if(conditioningMaterial) {
            containersString += `${conditioningMaterial}${conditioningMaterialSeparator}`
        }


        const containers = wemiSelectors.actualContainers(wemi)
        if(containers) {
            const separator = ', '

            for(const container of containers) {
                if(container.container) {
                    const containerInventoryNumber = await this.getItemFromId(container.container)
                    if(containerInventoryNumber) {
                        containersString += `${containerInventoryNumber}${separator}`
                    }
                }
            }
            containersString = containersString.slice(0, -separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.conditioning'),
            containersString, 
            { width: textWidth }
        )
    }

    private async writeInstitutionItemSection(doc, wemi: Wemi, textWidth: number) {
        let institutionString = ''
        const ownerInstitutions = wemiSelectors.ownerInstitutions(wemi)
        if(ownerInstitutions) {
            const separator = ', '

            for(const institution of ownerInstitutions) {
                if(institution.ownerInstitution) {
                    const institutionName = await this.getAliasFromId(institution.ownerInstitution)
                    if(institutionName) {
                        institutionString += `${institutionName}${separator}`
                    }
                }
            }
            institutionString = institutionString.slice(0,-separator.length)
        }
        doc.dataWithBoldLabel(
            this.$t('pdfDocument.objectSection.ownerInstitutions'),
            institutionString, 
            { width: textWidth }
        )
    }

    private  async createContextSection(doc, conditionReport: ConditionReport, startOnNewPage: boolean = true, sectionIndex?: number) {
        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN
        if(startOnNewPage) {
            doc.addPage()
        } else if(sectionIndex) {
            doc.moveDown(1)
        }
        doc.sectionTitle(this.$t('pdfDocument.contextSection.sectionTitle'))

        // Medias
        doc.moveDown(1);
        const associatedMedias = conditionReportSelectors.medias(conditionReport)



        await this.addImageBlock(doc, associatedMedias)

        // Report type
        const resolvedReportType = await this.getThesaurusTermFromId(conditionReportSelectors.reportType(conditionReport))
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportType'), resolvedReportType)

        // Report reason
        const resolvedReportReason = await this.getThesaurusTermFromId(conditionReportSelectors.reportReason(conditionReport))
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportReason'), resolvedReportReason)

        // Report date
        let formattedDate = ''
        const skindate = conditionReportSelectors.date(conditionReport)
        formattedDate = getDateString(skindate, ConditionReportPDFGenerator.appLocale)
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportDate'), formattedDate)

        // Report editors
        let editorsString: string = ''
        const editors = conditionReportSelectors.reportEditors(conditionReport)
        if(editors?.length) {
            const editorSeparator = " ; "

            for(const editor of editors) {
                if(editor.reportName) {
                    let editorString: string = await this.getAliasFromId(editor.reportName)
    
                    if(editorString) {
                        editorsString += editorString + editorSeparator
                    }
                }
            }
            if(editorsString.length) {
                editorsString = editorsString.slice(0, -editorSeparator.length)
            }
        }

        doc.dataWithBoldLabel(
            this.$t('pdfDocument.contextSection.editors'),
            editorsString
        )


        // Report signers
        let signersString: string = ''
        const signers = conditionReportSelectors.signers(conditionReport)
        if(signers?.length) {
            const signerSeparator = " ; "
            const namePositionSeparator = ", "

            for(const signer of signers) {
                let nameString: string = getTranslatedString(signer.signer, ConditionReportPDFGenerator.appLocale)
                let positionString = ''
                if(signer.positionOfSignatory) {
                    positionString = await this.getThesaurusTermFromId(signer.positionOfSignatory)
                } 

                if(nameString || positionString) {
                    if(nameString && positionString) {
                        signersString += nameString.trim() + namePositionSeparator + positionString.trim()
                    }
                    else if(nameString) {
                        signersString += nameString.trim()
                    }
                    else if(positionString) {
                        signersString += positionString.trim()
                    }
                    signersString += signerSeparator
                }
            }
            if(signersString.length) {
                signersString = signersString.slice(0, -signerSeparator.length)
            }
        }

        doc.dataWithBoldLabel(
            this.$t('pdfDocument.contextSection.signers'),
            signersString
        )

        // Report location
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportPlace'), conditionReportSelectors.location(conditionReport, ConditionReportPDFGenerator.appLocale))

        await this.createConditioningSection(doc, conditionReport, false, sectionIndex)
    }

    // For now this section is a part of the "context" section
    private async createConditioningSection(doc, conditionReport, startOnNewPage: boolean = false, sectionIndex?: number) {
        if(startOnNewPage) {
            doc.addPage()
        } else if(sectionIndex) {
            doc.moveDown(1)
        }
        doc.sectionTitle(this.$t('pdfDocument.conditioningSection.sectionTitle'))

        doc.moveDown(2)

        const typeOfPackaging = conditionReportSelectors.typeOfPackaging(conditionReport)
        const crateNumber = conditionReportSelectors.crateNumber(conditionReport)
        const transportAccessories = conditionReportSelectors.transportAccessories(conditionReport)

        if(typeOfPackaging) {
            const typeOfPackagingSeparator = ' ; '
            let typeOfPackagingString = ''

            if(typeOfPackaging.type) {
                const type = getTranslatedString(
                    typeOfPackaging.type,
                    ConditionReportPDFGenerator.appLocale
                )
                if(type) {
                    typeOfPackagingString += `${type}${typeOfPackagingSeparator}`
                }
            }

            if(typeOfPackaging.materials?.length) {
                for(const material of typeOfPackaging.materials) {
                    const materialValue = getTranslatedString(
                        material,
                        ConditionReportPDFGenerator.appLocale
                    )
                    if(materialValue) {
                        typeOfPackagingString += `${materialValue}${typeOfPackagingSeparator}`
                    }
                }
            }

            if(typeOfPackaging.furtherInformations?.length) {
                for(const comment of typeOfPackaging.furtherInformations) {
                    const commentValue = getTranslatedString(
                        comment,
                        ConditionReportPDFGenerator.appLocale
                    )
                    if(commentValue) {
                        typeOfPackagingString += `${commentValue}${typeOfPackagingSeparator}`
                    }
                }
            }

            if(typeOfPackagingString) {
                typeOfPackagingString = typeOfPackagingString.slice(0, -typeOfPackagingSeparator.length)
                doc.dataWithBoldLabel(
                    this.$t('pdfDocument.conditioningSection.typeOfPackaging'),
                    typeOfPackagingString
                )
            }
        }

        if(crateNumber) {
            const crateNumberString = getTranslatedString(crateNumber, ConditionReportPDFGenerator.appLocale) ?? ''
            doc.dataWithBoldLabel(
                this.$t('pdfDocument.conditioningSection.crateNumber'),
                crateNumberString
            )
        }

        if(transportAccessories?.length) {
            let transportAccessoriesString = ''
            const transportAccessorySeparator = ' ; '
            for(const transportAccessory of transportAccessories) {
                const value = getTranslatedString(transportAccessory, ConditionReportPDFGenerator.appLocale)
                if(value) {
                    transportAccessoriesString += `${value}${transportAccessorySeparator}`
                }
            }
            if(transportAccessoriesString) {
                transportAccessoriesString = transportAccessoriesString.slice(0, -transportAccessorySeparator.length)
                doc.dataWithBoldLabel(
                    this.$t('pdfDocument.conditioningSection.transportAccessories'),
                    transportAccessoriesString
                )
            }
        }
    }

    private async createAlterationsSection(doc, conditionReport: ConditionReport) {
        if(conditionReport.conditionReportCS?.alterations) {
            const mediaReferencedInAlterations = _.uniq(conditionReport.conditionReportCS.alterations
                .map(alteration => alteration.media)
                .filter(media => media != null))

            // we make sure medias are in the correct order
            const associatedMedias = _.get(conditionReport, "associatedMedias.medias", [])    
            const [sortedMediasReferencedInAlterations, mediasWithNoAlterations] = _.partition(
                associatedMedias, 
                mediaId => {
                    return mediaReferencedInAlterations.includes(mediaId)
                }
            )

            let mediasToPrint: string[] = sortedMediasReferencedInAlterations
            if(PRINT_NON_ALTERATION_MEDIAS) {
                switch(NON_ALTERATION_MEDIAS_ORDER) {
                    case "after":
                        mediasToPrint = mediasToPrint.concat(mediasWithNoAlterations)
                        break
                    case "before":
                        mediasToPrint = mediasWithNoAlterations.concat(mediasToPrint)
                        break
                }
            }

            const identation = ConditionReportPDFGenerator.DOCUMENT_MARGIN
            const textWidth = doc.page.width - identation - ConditionReportPDFGenerator.DOCUMENT_MARGIN

            const numberedAlterations = computeAlterationsIndexes(conditionReport)

            let detailedViews: AlterationNameAndDetailedViews[] = []

            for(const mediaId of mediasToPrint) {
                if(mediaId) {
                    doc.addPage()
                    doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))
                    doc.moveDown(1)

                    const alterationsLinkedToMedia = numberedAlterations.filter(alteration => alteration.media === mediaId)
                    await this.drawAlterationOnImage(doc, alterationsLinkedToMedia, mediaId, ALTERATION_MEDIA_TYPE)
                    let legendAdded = await this.addMediaLegendIfNeeded(doc, mediaId)

                    doc.x = identation
                    doc.y += legendAdded ? 
                        1
                        : ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE 
                    doc.moveDown(1)

                    let heightOfAlterationTextArea = doc.page.height - doc.y - ConditionReportPDFGenerator.DOCUMENT_MARGIN
                    let totalTextSizeTillNow = 0

                    for(const alteration of alterationsLinkedToMedia) {
                        const typeOfAlteration = alteration.typeOfAlteration ? await ThesaurusTermDataStore.getInstance().fetchOne(alteration.typeOfAlteration).toPromise() : undefined
                        const typeOfAlterationText = typeOfAlteration ? getTranslatedString(
                            thesaurusTermSelectors.preferredTerm(typeOfAlteration),
                            ConditionReportPDFGenerator.appLocale
                        ) : undefined
                        const title = alteration.title ? getTranslatedString(alteration.title, ConditionReportPDFGenerator.appLocale) : undefined
                        const precision = alteration.precision ? getTranslatedString(alteration.precision, ConditionReportPDFGenerator.appLocale) : undefined

                        let boldLine = `${alteration.index}.`
                        if(typeOfAlteration || title) {
                            boldLine += ` ${title ? title : ''} - ${typeOfAlterationText ? typeOfAlterationText : ''}`
                        }

                        const textOptions: any = {
                            width: textWidth,
                            align: 'justify',
                        }
                        const boldTextOptions: any = {
                            width: textWidth,
                            align: 'justify',
                        }
                        let boldLineHeight = doc.heightOfString(boldLine, boldTextOptions)
                        let precisionHeight = doc.heightOfString(precision, textOptions)
                        let alterationTextHeight = boldLineHeight + precisionHeight

                        totalTextSizeTillNow += alterationTextHeight
                        
                        if(totalTextSizeTillNow > heightOfAlterationTextArea) {
                            // we reached the bottom, we need to go to a new page
                            doc.addPage()
                            doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))
                            doc.moveDown(1)
                            await this.drawAlterationOnImage(doc, alterationsLinkedToMedia, mediaId, ALTERATION_MEDIA_TYPE)
                            legendAdded = await this.addMediaLegendIfNeeded(doc, mediaId)
                            doc.x = identation
                            doc.y += legendAdded ? 
                                1
                                : ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
                            doc.moveDown(1)

                            /*if(alterationTextHeight > heightOfAlterationTextArea) {
                                // Handle the case where the text is longer than one page
                            }*/
                        }
                        
                        doc.boldText(boldLine, boldTextOptions)
                        doc.regularText(precision, textOptions)
                        doc.moveDown(1)

                        if(alteration.detailedViews?.length) {
                            detailedViews.push({
                                alterationName: boldLine,
                                alterationNumber: alteration.index ?? -1,
                                detailedViews: alteration.detailedViews,
                            })
                        }
                    }

                    if(ConditionReportPDFGenerator.DETAILED_VIEW_PLACEMENT === DetailedVuePlacement.AFTER_EACH) {
                        if(detailedViews.length) {
                            await this.createAlterationDetailedViewSection(doc, detailedViews)
                            detailedViews = []
                        }
                    }
                }
            }

            if(ConditionReportPDFGenerator.DETAILED_VIEW_PLACEMENT === DetailedVuePlacement.END) {
                if(detailedViews.length) {
                    await this.createAlterationDetailedViewSection(doc, detailedViews)
                }
            }
        }
    }

    private async createAlterationDetailedViewSection(doc, detailedViewsByAlteration: AlterationNameAndDetailedViews[]) {
        const startDetailedViewX = ConditionReportPDFGenerator.DOCUMENT_MARGIN
        const maxHeight = doc.page.height - (2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN)
        const textWidth = doc.page.width - (2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN)

        const lineHeight = doc.heightOfString('m', { width: textWidth })
        const heightOfARow = ConditionReportPDFGenerator.DETAILED_VIEW_IMAGE_SIZE + ConditionReportPDFGenerator.DETAILED_VIEW_ROW_GAP
        const widthOfAColumn = ConditionReportPDFGenerator.DETAILED_VIEW_IMAGE_SIZE + ConditionReportPDFGenerator.DETAILED_VIEW_COL_GAP

        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.detailedViewsSection.sectionTitle'))
        doc.moveDown(1)

        let y: number = doc.y
        let x: number = startDetailedViewX

        for(const alteration of detailedViewsByAlteration) {
            // if x is at the start, it means we jumped a line on last detailed view of previous alteration
            // if not, we need to go to the next line 
            if(x === startDetailedViewX) {
                doc.y = y
            } else {
                doc.y = y + heightOfARow
                x = startDetailedViewX
            }

            if(y > maxHeight - (heightOfARow + 2 * lineHeight)) {
                doc.addPage()
                doc.sectionTitle(this.$t('pdfDocument.detailedViewsSection.sectionTitle'))
                doc.moveDown(1)

                y = doc.y
            }

            doc.text(
                alteration.alterationName,
                {
                    width: textWidth,
                    align: 'left',                    
                }
            )
            doc.moveDown(1)
            y = doc.y

            for(const [index, detailedView] of alteration.detailedViews.entries()) {
                await this.addImageFromMediaId(
                    doc,
                    detailedView,
                    x,
                    y,
                    ConditionReportPDFGenerator.DETAILED_VIEW_IMAGE_SIZE,
                    {
                        align: 'center',
                        fit: [
                            ConditionReportPDFGenerator.DETAILED_VIEW_IMAGE_SIZE, 
                            ConditionReportPDFGenerator.DETAILED_VIEW_IMAGE_SIZE
                        ],
                        valign: 'center'
                    },
                    ALTERATION_MEDIA_TYPE,
                )
    
                if(index % ConditionReportPDFGenerator.DETAILED_VIEW_IMAGES_PER_ROW === ConditionReportPDFGenerator.DETAILED_VIEW_IMAGES_PER_ROW - 1) {
                    y += heightOfARow
                    x = startDetailedViewX
    
                    if(y > maxHeight - heightOfARow) {
                        doc.addPage()
                        doc.sectionTitle(this.$t('pdfDocument.detailedViewsSection.sectionTitle'))
                        doc.moveDown(1)
    
                        y = doc.y
                    }
                } else {
                    x += widthOfAColumn
                }
            }
        }
    }

    private  async createReportSection(doc, conditionReport: ConditionReport, startOnNewPage: boolean = true, sectionIndex?: number) {
        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN
        if(startOnNewPage) {
            doc.addPage()
        } else if(sectionIndex) {
            doc.moveDown(1)
        }
        doc.sectionTitle(this.$t('pdfDocument.reportSection.sectionTitle'))

        doc.moveDown(1)
        const theComments = conditionReportSelectors.comments(conditionReport, ConditionReportPDFGenerator.appLocale)
        if (theComments) {
            doc.regularText(theComments, { align: "justify" })
        }

        doc.moveDown(1)
        const conservationStates = await conditionReportSelectors.stateOfConservation(conditionReport)
        doc.boldText(this.$t('pdfDocument.reportSection.stateOfConservation'));
        doc.moveDown(0.5)
        if (conservationStates) {
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN + 8;
            for (const state of conservationStates) {
                const stateTh = await this.getThesaurusTermFromId(state.stateOfConservation);
                doc.boldText(stateTh)
                doc.regularText(getTranslatedString(state.precision, ConditionReportPDFGenerator.appLocale), { align: "justify" })
                doc.moveDown(0.5)
            }
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        }
        doc.moveDown()
        const priorityOfTreatment = await this.getThesaurusTermFromId(conditionReportSelectors.treatmentPriority(conditionReport))
        if (priorityOfTreatment) {
            doc.boldText(this.$t('pdfDocument.reportSection.treatmentPriority'), { continued: true });
            doc.regularText(priorityOfTreatment)
            doc.moveDown(1)
        }

        const theInterventionsToSchedule: any = await conditionReportSelectors.interventionsToSchedule(conditionReport)
        if (theInterventionsToSchedule) {
            doc.boldText(this.$t('pdfDocument.reportSection.interventionsToSchedule'));
            doc.moveDown(0.5)
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN + 8;
            for (const intervention of theInterventionsToSchedule) {
                const interventionType = await this.getThesaurusTermFromId(get(intervention, "typeIntervention"))
                if (interventionType) {
                    doc.boldText(interventionType)
                }
                doc.regularText(getTranslatedString(intervention.precision, ConditionReportPDFGenerator.appLocale), { align: "justify" })
                doc.moveDown(0.5)

            }
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;

            doc.moveDown(1)
        }
        doc.moveDown(3)
        doc.boldText(this.$t('pdfDocument.reportSection.signatoriesTitle'));
        doc.moveDown(1)
        // signatures
        const signers = get(conditionReport, 'conditionReportCS.signers')
        let lineY = doc.y;
        if (signers) {
            for (const signer of signers) {
                if (doc.y !== lineY) {
                    doc.moveDown(2);
                    lineY = doc.y;
                }
                const headerY = doc.y;
                let signerText = getTranslatedString(signer.signer, ConditionReportPDFGenerator.appLocale)
                if(signer.positionOfSignatory) {
                    const positionOfSignatory = await this.getThesaurusTermFromId(signer.positionOfSignatory)
                    signerText += ', ' + positionOfSignatory
                }
                doc
                    .fill('black')
                    .text(signerText, { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH })

                if (signer.signature) {
                    const unMarshalledSignature = JSON.parse(signer.signature);
                    if (unMarshalledSignature
                        && unMarshalledSignature.signature
                        && unMarshalledSignature.signature.length > 0) {
                        this.generateSignature(doc, unMarshalledSignature, headerY)
                    } else {
                        this.generateSignature(doc, null, headerY)
                    }
                }
            }
            doc.moveDown(5) // to prevent next section to start on the signature
        }
        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN

        await this.createCounterSignatureSection(doc, conditionReport)
    }

    private async createCounterSignatureSection(doc, conditionReport: ConditionReport) {
        doc.moveDown(1)
        const counterSignatures:CounterSignatureResource[] = get(conditionReport, 'conditionReportCS.counterSignatures', [])
        if (counterSignatures && counterSignatures.length) {
            doc.moveDown(3)
            doc.sectionTitle(this.$t('pdfDocument.reportSection.counterSignaturesTitle'))
            doc.moveDown(1)

            for(const [csIndex, counterSignature] of counterSignatures.entries()) {
                const fullTextWidth = doc.page.width - 2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN
                const lineHeight = doc.heightOfString('m', { width: fullTextWidth })

                let counterSignatureText = await this.getThesaurusTermFromId(counterSignature.reason)
                counterSignatureText += ', ' + getTranslatedString(counterSignature.location, ConditionReportPDFGenerator.appLocale)
                if(counterSignature.date?.skinIntervalComplexes?.[0]?.startInterval?.date) {
                    const dateFormat = this.getDateFormatFromServer()
                    counterSignatureText += ', ' + toFormat(counterSignature.date, dateFormat)
                }
                const commentLabel = this.$t('pdfDocument.reportSection.counterSignature.comment')
                const commentValue = getTranslatedString(counterSignature.comment, ConditionReportPDFGenerator.appLocale)
                const signatoriesTitle = this.$t('pdfDocument.reportSection.signatoriesTitle')

                const signers = counterSignature.observationCarriedPresenceOfs ?? []
                const signerTexts: string[] = []
                for(const signer of signers) {
                    let signerText = getTranslatedString(signer.surnameFirstName, ConditionReportPDFGenerator.appLocale)
                    if(signer.function) {
                        const positionOfSignatory = await this.getThesaurusTermFromId(signer.function)
                        signerText += ', ' + positionOfSignatory
                    }
                    signerTexts.push(signerText)
                }

                let estimatedHeight = doc.heightOfString(
                    counterSignatureText,
                    { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH }
                ) + lineHeight
                if(commentValue) {
                    estimatedHeight += doc.heightOfString(
                        `${commentLabel} ${commentValue}`,
                        { width: fullTextWidth }
                    ) + lineHeight
                }
                estimatedHeight += doc.heightOfString(signatoriesTitle, { width: fullTextWidth })
                if(signers.length) {
                    estimatedHeight += this.estimatedSignerRowHeight(doc, signers, signerTexts, 0)
                }
                if(csIndex != 0) {
                    estimatedHeight += 2 * lineHeight
                }

                if(doc.y + estimatedHeight > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN ) {
                    doc.addPage()
                }
                if(csIndex != 0) {
                    this.drawLineSeparator(doc, ConditionReportPDFGenerator.DOCUMENT_MARGIN, 1)
                }

                doc.fill('black')
                    .regularText(
                        counterSignatureText,
                        { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH }
                    )
                doc.moveDown(1)

                doc.dataWithBoldLabel(
                    commentLabel,
                    commentValue,
                    { width: fullTextWidth }
                )

                doc.moveDown(1)

                // counter signers
                doc.boldText(signatoriesTitle)
                if(signers.length) {
                    let lineY = doc.y
                    for (const [signerIndex, signer] of signers.entries()) {
                        if (signerIndex === 0 || doc.y !== lineY) {
                            const estimatedHeight2 = this.estimatedSignerRowHeight(doc, signers, signerTexts, signerIndex)
                            if(doc.y + estimatedHeight2 > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN ) {
                                doc.addPage()
                                doc.moveDown(0.1)
                            } else if(signerIndex !== 0) {
                                doc.moveDown(2);
                            }
                            lineY = doc.y;
                        }
                        const headerY = doc.y;
                        const signerText = signerTexts[signerIndex]
                        doc
                            .fill('black')
                            .regularText(signerText, { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH })

                        if (signer.signature) {
                            const signature = signer.signature
                            if(signature) {
                                const unMarshalledSignature = JSON.parse(signature);
                                if (unMarshalledSignature
                                    && unMarshalledSignature.signature
                                    && unMarshalledSignature.signature.length > 0) {
                                    this.generateSignature(doc, unMarshalledSignature, headerY)
                                } else {
                                    this.generateSignature(doc, null, headerY)
                                }
                            }
                        }

                        if(signerIndex === signers.length - 1) {
                            // if last signature reset margin
                            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN

                            // Go to next line if not already done
                            if(signerIndex % 2 === 0 || !signer.signature) {
                                doc.y += ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT
                                doc.moveDown(1)
                            }
                        }
                    }
                }
            }
        }
    }

    // Two signers share a row so the row starting at rowStartIndex needs enough height for whichever
    // of the two signers is taller.
    private estimatedSignerRowHeight(doc, signers: any[], signerTexts: string[], rowStartIndex: number): number {
        const rowIndices = [rowStartIndex, rowStartIndex + 1].filter(i => i < signers.length)
        return Math.max(...rowIndices.map(i =>
            signers[i].signature
                ? ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT
                : doc.heightOfString(signerTexts[i], { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH })
        ))
    }

    private  async addImageBlock(doc, mediasIds: string[], fileType: string = 'vignette250') {
        doc.moveDown(1)
        const imgStep = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP;
        for (const mediaId of mediasIds) {
            await this.addImageFromMediaId(
                doc, 
                mediaId,
                undefined,
                undefined,
                undefined,
                undefined,
                fileType
            )

            doc.x += imgStep;

            // Continuing on a new line if needed
            if (doc.x + imgStep > doc.page.width - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                doc.y += imgStep;
            }

            // Continuing on a new page if needed
            if (doc.y + imgStep > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                doc.addPage()
                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                doc.y += ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            }
        }
        if (doc.x !== ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            doc.y += imgStep;
        }

    }

    private  async addImageFromMediaId(doc, mediaId: string, x?: number, y?: number,
        imageSize: number = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, options?: any,
        fileType: string = 'vignette250'
    ) {
        if (this.isDryRun) return

        const isLocalMedia = mediaId.startsWith('local:')
        const downloadOriginal = fileType === 'original'

        // avoid endlessly searching cached media id on API using the filename
        let downloadMedia$: Observable<any> = ImageTransferHelper.getInstance().downloadImage(mediaId, fileType, isLocalMedia)
        if(downloadOriginal && !isLocalMedia) {
            downloadMedia$ = ImageTransferHelper.getInstance().downloadOriginalImage(mediaId)
        }
        const mediaData = await downloadMedia$.pipe(take(1)).toPromise();
        
        this.addImageFromBase64(doc, mediaData.dataUrl, x, y, imageSize, options)
    }

    private addImageFromBase64(doc, mediaBase64: string, x?: number, y?: number, 
        imageSize: number = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, options?: any
    ) {
        const saveY = doc.y;
        doc.save();
        const imgX = x ? x : doc.x;
        const imgY = y ? y : doc.y;

        const centerX = imgX + imageSize / 2;
        const centerY = imgY + imageSize / 2;

        const imgOptions = options ? options :
            {
                align: 'center',
                fit: [imageSize, imageSize],
                valign: 'center'
            };
        
        let rotationAngle = 0
        const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(mediaBase64)

        // Pass mediaWithoutOrientation as-is (ArrayBuffer or string): PDFKit caches string image
        // sources in its internal registry, which accumulates memory across a document's images,
        // so we avoid re-encoding the ArrayBuffer back to a string here.
        const finalMedia: string | ArrayBuffer = mediaWithoutOrientation

        if(orientation !== -1) {
            switch (orientation) {
                case 3: rotationAngle = 180; break;
                case 6: rotationAngle = 90; break;
                case 8: rotationAngle = -90; break;
            }
        }

        if(ConditionReportPDFGenerator.PRINT_IMAGES_OUTLINE) {
            doc.rect(imgX, imgY, imageSize, imageSize).fill("lightgray");
        }
        if (finalMedia) {
            doc
                .rotate(rotationAngle, { origin: [centerX, centerY] })
                .image(finalMedia, imgX, imgY, imgOptions)
        }
        doc.restore()
        doc.y = saveY
    }

    private async getRotationAngleFromOrientation(imageDataUrl) {

        const img = new Image();
        img.src = imageDataUrl;
        const orientation = await getImageOrientation(img)
        switch (orientation) {
            case 3:
                return 180
            case 6:
                return 90
            case 8:
                return -90
            default:
                return 0
        }
    }

    private  generateSignature(doc, signatureDrawingData: SignatureDrawing | null, headerY) {

        if (signatureDrawingData) {
            // Defensive: don't rely on the previous consumer of the shared hidden stage having
            // cleaned up after itself.
            this.resetHiddenCanvases()
            // Resizing konva stage with signature format
            this.hiddenStage.width(signatureDrawingData.canvas.width)
            this.hiddenStage.height(signatureDrawingData.canvas.height)
            signatureDrawingData.signature.map(signatureShape => {
                this.hiddenStageShapesLayer.add(new Konva.Line(signatureShape));
            })
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStageShapesLayer.draw();
            const signatureImgDataUrl = this.hiddenStage.toDataURL({ pixelRatio: ConditionReportPDFGenerator.SIGNATURE_GENERATION_PIXEL_RATIO });

            doc.image(signatureImgDataUrl, doc.x, doc.y, {
                align: 'center',
                height: ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT,
                valign: 'center',
                width: signatureDrawingData.canvas.width * ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT / signatureDrawingData.canvas.height,
            })
            // Also clean up after use, to release the pixel buffers right away instead of leaving
            // them allocated until the next consumer's own defensive cleanup runs.
            this.resetHiddenCanvases()
        }
        doc.x += ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP
        doc.y = headerY;
        if (doc.x + ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH > doc.page.width - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {

            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            doc.y += ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP;
            doc.moveDown(1)
        }
    }

    private  async getThesaurusTermFromId(id): Promise<string> {
        if (id) {
            return getTranslatedString(
                thesaurusTermSelectors.preferredTerm(
                    await ThesaurusTermDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
                ),
                ConditionReportPDFGenerator.appLocale
            )
        } else {
            return ""
        }
    }

    private async getAliasFromId(id: string): Promise<string> {
        if(id) {
            const alias = await AliasDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
            if(alias) {
                const title = aliasSelectors.translatedTitle(alias)
                if(title) {
                    return getTranslatedString(
                        title,
                        ConditionReportPDFGenerator.appLocale
                    )
                }
            }
        }

        return ''
    }

    private async getLocationFromId(id: string): Promise<string> {
        if(id) {
            const location = await LocationDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
            if(location) {
                return locationSelectors.fullPath(location, ConditionReportPDFGenerator.appLocale)
            }
        }

        return ''
    }

    private async getItemFromId(id: string): Promise<string> {
        if(id) {
            const item: Item = await ItemDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
            if(item) {
                return itemSelectors.inventoryNumber(item)
            }
        }

        return ''
    }

    /**
     *
     * @param doc
     * @param mediaId
     * @returns A boolean indicated if the media legend has been added
     */
    private async addMediaLegendIfNeeded(doc, mediaId: string): Promise<boolean> {
        if (this.isDryRun) return false

        if(!MediaTransferHelper.isLocalMedia(mediaId)) {
            const mediaNotice = await MediaDataStore.getInstance().fetchOne(mediaId).toPromise()

            if(mediaNotice.mediaCS?.legendMedia?.[0]?.legend) {
                const legend = getTranslatedString(
                    mediaNotice.mediaCS.legendMedia[0].legend,
                    ConditionReportPDFGenerator.appLocale
                )

                if(legend && (typeof legend === 'string' || legend instanceof String)) {
                    doc.y += ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
                    doc.x = 0
                    doc.moveDown(1)
                    doc.regularText(
                        legend, 
                        {
                            align: 'center',
                        }
                    )
                    return true
                }
            }
        }
        return false
    }

    // Decodes a data URL via <img> rather than atob(): the browser decodes it natively instead of
    // going through an intermediate JS string/typed array copy. It uses less RAM
    private loadHTMLImage(src: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = src
        })
    }

    // Split out of drawAlterationOnImage not just for clarity/reuse: it also bounds how long the
    // large intermediate download buffers stay reachable, which matters for peak memory here.
    private async downloadAndDecodeAlterationImage(mediaId: string, fileType: string): Promise<{
        imageBitmap: ImageBitmap
        orientation: number
        width: number
        height: number
    }> {
        const downloadOriginal = fileType === 'original'
        let downloadMedia$: Observable<any> = ImageTransferHelper.getInstance().downloadImage(mediaId, fileType)
        if (downloadOriginal && !MediaTransferHelper.isLocalMedia(mediaId)) {
            downloadMedia$ = ImageTransferHelper.getInstance().downloadOriginalImage(mediaId)
        }
        const media = await downloadMedia$.pipe(first()).toPromise()
        const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(media.dataUrl)

        // We need to return an ArrayBuffer (for performances issues, RAM related)
        // The use of loadHTMLImage is also important for performances, attention must be paid to it 
        // in case this call would need to be replaced
        const imageBitmap = (mediaWithoutOrientation instanceof ArrayBuffer)
            ? await createImageBitmap(new Blob([mediaWithoutOrientation], { type: 'image/jpeg' }))
            : await createImageBitmap(await this.loadHTMLImage(mediaWithoutOrientation))
        return { imageBitmap, orientation, width: imageBitmap.width, height: imageBitmap.height }
    }

    private async drawAlterationOnImage(doc, alterations: Alteration[], mediaId: string, fileType: string = ALTERATION_MEDIA_TYPE) {
        if (this.isDryRun) {
            // Simulate cursor position only — images always restore doc.y so layout is identical
            const lineY = doc.y
            const pageWidth = ConditionReportPDFGenerator.A4_PAPER_SIZE[0] - (2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN)
            const availableWidth = pageWidth - ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
            const imageMargin = availableWidth / 2
            doc.x += imageMargin
            doc.y = lineY
            if (doc.y + ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                doc.addPage()
                doc.x = imageMargin
                doc.y = ConditionReportPDFGenerator.DOCUMENT_MARGIN
            }
            doc.fill('black')
            return
        }

        // Defensive: don't rely on the previous consumer of the shared hidden stage having
        // cleaned up after itself.
        this.resetHiddenCanvases()

        const { imageBitmap, orientation, width, height } = await this.downloadAndDecodeAlterationImage(mediaId, fileType)

        // Drawing bg image
        const imgConfig = {
            height,
            image: imageBitmap,
            width,
            x: 0,
            y: 0,
        }
        // Size the stage directly to output resolution — never allocates at native resolution.
        // Shape coordinates stay in native space; the layer scale projects them onto the smaller canvas.
        const pixelRatio = this.getPixelRatio(imgConfig.width, imgConfig.height)
        const fixedConfig = await this.fixImageOrientation(imgConfig, orientation, pixelRatio)
        this.hiddenStageShapesLayer.scale({ x: pixelRatio, y: pixelRatio })
        this.hiddenStageAnnotationsLayer.scale({ x: pixelRatio, y: pixelRatio })

        this.hiddenStageShapesLayer.add(new Konva.Image(fixedConfig))

        for(const alteration of alterations) {
            if(alteration.drawing) {
                // Retrieving drawing data
                const svg = JSON.parse(alteration.drawing)

                let firstShape = true
                for(const shapeSvg of svg) {
                    if(shapeSvg) {
                        const drawnShape = this.getShapeFromSvg(shapeSvg)
                        this.hiddenStageShapesLayer.add(drawnShape)
    
                        if(firstShape) {
                            // add number label
                            const labelRadius = ConditionReportPDFGenerator.ANNOTATION_LABEL_RADIUS_RATIO * imgConfig.width;
                            const labelCenter = getLabelCoordinates(shapeSvg, labelRadius)
                            const labelNode = new Konva.Circle({
                                fill: "white",
                                radius: labelRadius,
                                stroke: "black",
                                strokeWidth: ConditionReportPDFGenerator.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * imgConfig.width,
                                x: labelCenter.x,
                                y: labelCenter.y,
                            })
    
                            /* About the positionning of text inside the circle:
                             * The top of the text start at x and the left of the text start at y,
                             * so we put the text inside an invisible square of side labelRadius * 2 (= diameter of the circle)
                             * then we use align and vertical align to center the text inside the square
                             * we dont have to forget to make the square coordinate start at the begining of square diameter (so x/y = center - radius) 
                            */
                            const fontSize = ConditionReportPDFGenerator.ANNOTATION_LABEL_FONT_SIZE_RATIO * imgConfig.width
                            const yText = labelCenter.y - labelRadius
                            const xText = labelCenter.x - labelRadius
                            const textNode = new Konva.Text({
                                width: labelRadius * 2,
                                height: labelRadius * 2,
                                align: 'center',
                                verticalAlign: 'middle',
                                x: xText,
                                y: yText,
                                fill: 'black',
                                fontFamily: 'Calibri',
                                fontSize: fontSize,
                                offsetX: 0,
                                offsetY: 0,
                                text: (alteration.index ?? '#').toString(),
                            });
    
                            this.hiddenStageAnnotationsLayer.add(labelNode);
                            this.hiddenStageAnnotationsLayer.add(textNode);
                            firstShape = false
                        }
                    }
                }
            }
        }

        this.hiddenStageShapesLayer.draw();
        this.hiddenStageAnnotationsLayer.draw();
        // toDataURL() re-renders the current node tree onto a fresh canvas, it doesn't read an
        // already-drawn one — so it must run before resetHiddenCanvases() below removes those nodes,
        // or the resulting image would be blank.
        const dataUrl = this.hiddenStage.toDataURL({ pixelRatio: 1, mimeType: "image/jpeg" })
        imageBitmap.close()
        this.resetHiddenCanvases()
        // Convert to an ArrayBuffer (not the base64 string) before handing it to PDFKit: PDFKit caches
        // string image sources in its internal registry, which accumulates memory across many images.
        const generatedImgBuffer = convertBase64ToArrayBuffer(dataUrl)
        await new Promise<void>(r => setTimeout(r, 0))

        const lineY = doc.y;

        const pageWidth = ConditionReportPDFGenerator.A4_PAPER_SIZE[0] - (2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN)
        const availableWidth = pageWidth - ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
        const imageMargin = availableWidth/2

        doc.x += imageMargin

        // Drawing image on pdf
        if(ConditionReportPDFGenerator.PRINT_IMAGES_OUTLINE) {
            doc.rect(doc.x, doc.y, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE)
                .fill("lightgray")
        }
        doc.image(generatedImgBuffer, doc.x, doc.y, {
            align: 'center',
            fit: [ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE],
            valign: 'center'
        })

        doc.y = lineY;

        // If line overflows vertically, go on a new page
        if (doc.y + ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
            doc.addPage()
            doc.x = imageMargin;
            doc.y = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        }
        // reset color for next steps
        doc.fill('black')
    }

    private getPixelRatio(imgWidth: number, imgHeight: number) {
        // Always cap the canvas at targetPixelNumber, regardless of config, to bound peak memory:
        // a larger proxy would allocate several full-resolution canvases at once (stage, layers, composite).
        // sqrt because pixelRatio scales both dimensions: canvas_pixels = (w·ratio)·(h·ratio) = img_pixels·ratio²
        // → ratio = sqrt(target / img_pixels), not target/img_pixels (which would undershoot by a factor of ratio).
        const capRatio = Math.sqrt(ConditionReportPDFGenerator.IMAGE_GENERATION_TARGET_PIXEL_NUMBER / (imgWidth * imgHeight))
        const cap = capRatio < 1 ? capRatio : 1
        if(COMPUTE_PIXEL_RATIO || PIXEL_RATIO <= 0) {
            return cap
        }
        return Math.min(PIXEL_RATIO, cap)
    }

    private async fixImageOrientation(baseImageConfig, pOrientation?: number, scale: number = 1) {
        let orientation = 1
        if(pOrientation) {
            orientation = pOrientation
        } else if(!isImageOrientationAutoManaged()) {
            orientation = await getImageOrientation(baseImageConfig.image)
        }
        switch (orientation) {
            case 3: {
                this.hiddenStage.width(baseImageConfig.width * scale)
                this.hiddenStage.height(baseImageConfig.height * scale)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: baseImageConfig.height,
                    rotation: 180
                }
            }
            case 6: {
                this.hiddenStage.width(baseImageConfig.height * scale)
                this.hiddenStage.height(baseImageConfig.width * scale)

                return {
                    ...baseImageConfig,

                    offsetX: 0,
                    offsetY: baseImageConfig.height,
                    rotation: 90
                }
            }
            case 8: {
                this.hiddenStage.width(baseImageConfig.height * scale)
                this.hiddenStage.height(baseImageConfig.width * scale)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: 0,
                    rotation: -90,
                }
            }
            default:
                this.hiddenStage.width(baseImageConfig.width * scale)
                this.hiddenStage.height(baseImageConfig.height * scale)
                return baseImageConfig;
        }
    }

    private  getShapeFromSvg(shapeSvg) {
        switch (shapeSvg.type) {
            case DrawingToolType.RECTANGLE:
                return new Konva.Rect(shapeSvg);
            case DrawingToolType.CIRCLE:
                return new Konva.Circle(shapeSvg)
            case DrawingToolType.FREE_DRAWING:
                return new Konva.Line(shapeSvg)
            case DrawingToolType.ARROW:
                return new Konva.Arrow(shapeSvg)
            default:
                return new Konva.Circle(shapeSvg)
        }
    }

    private  $t(value: string) {
        let res = _.get(
            ConditionReportPDFGenerator.I18N_MESSAGES, 
            `${ConditionReportPDFGenerator.i18nLocale}.${value}`
        )

        if(res) {
            return res
        }

        const fallbackLocales = ConditionReportPDFGenerator.I18N_FALLBACK_CONF[ConditionReportPDFGenerator.i18nLocale] ?? ConditionReportPDFGenerator.I18N_FALLBACK_CONF["default"]
        for(const locale of fallbackLocales) {
            res = _.get(
                ConditionReportPDFGenerator.I18N_MESSAGES, 
                `${locale}.${value}`
            )

            if(res) {
                return res
            }
        }

        return res
    }

    private drawLineSeparator(doc, spaceFromEdge = 0, linesAboveAndBelow = 0.5) {
        doc.moveDown(linesAboveAndBelow);
      
        doc.moveTo(0 + spaceFromEdge, doc.y)
          .lineTo(doc.page.width - spaceFromEdge, doc.y)
          .stroke();
      
        doc.moveDown(linesAboveAndBelow);
        
        return doc
    }

    private getDateFormatFromServer(unit: string = DATE_UNIT.DAY): string {
        return getServerDateFormat(ConditionReportPDFGenerator.appLocale, unit)
    }

    private async getPDFMediasIds(associatedMedias: AssociatedMediasResource): Promise<string[]> {
        const PDF_REGEX = /\.pdf$/
        const mediaDataStore = MediaDataStore.getInstance()
        let pdfIds: string[] = []

        if(associatedMedias.medias?.length) {
            const mediasWithoutLocalMedias = associatedMedias.medias.filter(mediaId => {
                return !MediaTransferHelper.isLocalMedia(mediaId)
            })

            if(mediasWithoutLocalMedias.length) {
                pdfIds = await mediaDataStore.fetchMany(associatedMedias.medias).pipe(
                    map(response => {
                        let medias: Media[] = []
                        if(response.allData?.length) {
                            medias = response.allData
                        } 
                        else if(response.results?.length) {
                            medias = response.results
                        }
    
                        if(medias?.length) {
                            return medias.filter(media => {
                                const fileName = mediaSelectors.fileName(media)
                                if(typeof fileName === "string" || fileName instanceof String) {
                                    if(fileName.trim().match(PDF_REGEX)) {
                                        return true
                                    }
                                }
                                return false
                            }).map(media => {
                                return mediaSelectors.id(media)
                            })
                        }
    
                        return []
                    }),
                    first(),
                ).toPromise()
            }
        }
        return pdfIds
    }

    private async removePDFsFromMedias(associatedMedias: AssociatedMediasResource): Promise<AssociatedMediasResource> {
        const pdfIds = await this.getPDFMediasIds(associatedMedias)
        let cleanedAssociatedMedias: AssociatedMediasResource = _.cloneDeep(associatedMedias)


        if(associatedMedias.first) {
            if(pdfIds.includes(associatedMedias.first)) {
                cleanedAssociatedMedias.first = undefined
            }
        }
        if(associatedMedias.second) {
            if(pdfIds.includes(associatedMedias.second)) {
                cleanedAssociatedMedias.second = undefined
            }
        }
        if(associatedMedias.third) {
            if(pdfIds.includes(associatedMedias.third)) {
                cleanedAssociatedMedias.third = undefined
            }
        }

        if(associatedMedias.medias?.length) {
            cleanedAssociatedMedias.medias = associatedMedias.medias.filter(mediaId => {
                return !pdfIds.includes(mediaId)
            })
        }
        
        return cleanedAssociatedMedias
    }
}



